/**
 * BudgetPage — team budget / finance (coach/manager only).
 *
 * Tabs:
 *   • Overview     — balance + income/expense per currency, by-category
 *   • Transactions — ledger: add/edit/delete income & expenses, filters, CSV
 *   • Receipts     — upload a receipt photo/PDF, AI-scan → prefill an expense
 *   • Categories   — manage income/expense categories
 *   • Wallet       — Game Day managed "trust" wallet (Model B), opt-in
 *
 * Backend: /api/teams/:teamId/budget/*  (see backend/src/routes/budget.js)
 * Money is integer cents; the UI shows dollars.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  DollarSign, Plus, Trash2, Pencil, Receipt as ReceiptIcon, Tag,
  BarChart3, Wallet as WalletIcon, Upload, Sparkles, X, Download, Shield,
} from 'lucide-react';
import api from '../../services/api.js';
import { useTeamRole } from '../../hooks/useTeamRole.js';
import toast from 'react-hot-toast';
import { PageSpinner } from '../../components/Skeleton.jsx';

const fmtMoney = (cents, currency = 'cad') => {
  const amount = (cents || 0) / 100;
  const ccy = (currency || 'cad').toUpperCase();
  return `${ccy} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const centsFromInput = (v) => {
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const card = { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 };
const btn = { padding: '8px 14px', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', border: '1px solid var(--border)' };
const btnPrimary = { ...btn, background: 'var(--ice)', color: '#001018', border: '1px solid var(--ice)' };
const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: 14 };
const label = { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 4 };

export default function BudgetPage() {
  const { teamId } = useParams();
  const outlet = useOutletContext() || {};
  const currentSeasonId = outlet.currentSeasonId || null;
  const { isCoach, loading: roleLoading } = useTeamRole(teamId);

  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ byCurrency: [], byCategory: [] });
  const [txns, setTxns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [wallet, setWallet] = useState(undefined); // undefined=loading, null=not enabled, obj=enabled
  const [collections, setCollections] = useState(null); // actual fees collected (Stripe/registrations)

  // filters
  const [fSeason, setFSeason] = useState('all'); // 'all' | season_id
  const [fKind, setFKind] = useState('all');
  const [fCategory, setFCategory] = useState('all');

  // modal
  const [editTxn, setEditTxn] = useState(null); // {} for new, object for edit

  const loadCategories = useCallback(async () => {
    try { const { data } = await api.get(`/teams/${teamId}/budget/categories`); setCategories(data.categories || []); }
    catch { /* surfaced elsewhere */ }
  }, [teamId]);

  const loadSummary = useCallback(async () => {
    const params = {};
    if (fSeason !== 'all') params.season_id = fSeason;
    try { const { data } = await api.get(`/teams/${teamId}/budget/summary`, { params }); setSummary(data || { byCurrency: [], byCategory: [] }); }
    catch { /* */ }
  }, [teamId, fSeason]);

  const loadTxns = useCallback(async () => {
    const params = {};
    if (fSeason !== 'all') params.season_id = fSeason;
    if (fKind !== 'all') params.kind = fKind;
    if (fCategory !== 'all') params.category_id = fCategory;
    try { const { data } = await api.get(`/teams/${teamId}/budget/transactions`, { params }); setTxns(data.transactions || []); }
    catch { /* */ }
  }, [teamId, fSeason, fKind, fCategory]);

  const loadReceipts = useCallback(async () => {
    try { const { data } = await api.get(`/teams/${teamId}/budget/receipts`); setReceipts(data.receipts || []); }
    catch { /* */ }
  }, [teamId]);

  const loadWallet = useCallback(async () => {
    try { const { data } = await api.get(`/teams/${teamId}/budget/wallet`); setWallet(data.wallet ? data : null); }
    catch { setWallet(null); }
  }, [teamId]);

  // Actual fees collected through the app (Stripe + recorded payments) — reuses
  // the existing coach finance endpoint so the budget can compare planned income
  // vs money truly collected (per-player detail lives on the Finance tab).
  const loadCollections = useCallback(async () => {
    try { const { data } = await api.get(`/payments/team/${teamId}/finance`); setCollections(data || null); }
    catch { setCollections(null); }
  }, [teamId]);

  useEffect(() => {
    if (!isCoach) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([loadCategories(), loadSummary(), loadTxns(), loadReceipts(), loadWallet(), loadCollections()]);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [isCoach, loadCategories, loadSummary, loadTxns, loadReceipts, loadWallet, loadCollections]);

  // refetch list + summary when filters change (after initial load)
  useEffect(() => { if (isCoach) { loadTxns(); loadSummary(); } }, [fSeason, fKind, fCategory]); // eslint-disable-line

  const seasons = useMemo(() => {
    const map = new Map();
    txns.forEach(t => { if (t.season_id) map.set(t.season_id, t.season_id); });
    return Array.from(map.keys());
  }, [txns]);

  if (roleLoading || (loading && isCoach)) return <PageSpinner />;
  if (!isCoach) {
    return (
      <div style={{ maxWidth: 520, margin: '60px auto', textAlign: 'center', ...card }}>
        <Shield size={28} style={{ color: 'var(--text-3)' }} />
        <h2 style={{ margin: '12px 0 6px', fontSize: 18 }}>Coaches only</h2>
        <p style={{ color: 'var(--text-2)', fontSize: 14 }}>Team budget &amp; finances are visible to coaches and managers only.</p>
      </div>
    );
  }

  const tabs = [
    ['overview', 'Overview', BarChart3],
    ['transactions', 'Transactions', DollarSign],
    ['receipts', 'Receipts', ReceiptIcon],
    ['categories', 'Categories', Tag],
    ['wallet', 'Wallet', WalletIcon],
  ];

  const refreshAll = () => { loadTxns(); loadSummary(); loadReceipts(); };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '8px 4px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0 }}>Budget</h1>
          <p style={{ color: 'var(--text-2)', margin: '4px 0 0', fontSize: 14 }}>Track the team's income, expenses, and receipts.</p>
        </div>
        <button style={btnPrimary} onClick={() => setEditTxn({ kind: 'expense', txn_date: todayISO(), currency: 'cad' })}>
          <Plus size={15} style={{ verticalAlign: -2, marginRight: 4 }} /> Add expense
        </button>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
        {tabs.map(([key, lbl, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ ...btn, background: tab === key ? 'var(--ice-a12, rgba(56,189,248,0.12))' : 'transparent',
              border: `1px solid ${tab === key ? 'var(--ice-a30, rgba(56,189,248,0.3))' : 'var(--border)'}`,
              color: tab === key ? 'var(--ice)' : 'var(--text-2)' }}>
            <Icon size={14} style={{ verticalAlign: -2, marginRight: 5 }} />{lbl}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab summary={summary} collections={collections} />}
      {tab === 'transactions' && (
        <TransactionsTab
          txns={txns} categories={categories} seasons={seasons}
          fSeason={fSeason} setFSeason={setFSeason} fKind={fKind} setFKind={setFKind}
          fCategory={fCategory} setFCategory={setFCategory}
          onEdit={(t) => setEditTxn(t)} teamId={teamId} onChanged={refreshAll}
        />
      )}
      {tab === 'receipts' && (
        <ReceiptsTab teamId={teamId} receipts={receipts} onChanged={loadReceipts}
          categories={categories}
          onPrefill={(pref) => setEditTxn(pref)} />
      )}
      {tab === 'categories' && <CategoriesTab teamId={teamId} categories={categories} onChanged={loadCategories} />}
      {tab === 'wallet' && <WalletTab teamId={teamId} wallet={wallet} onChanged={loadWallet} />}

      {editTxn && (
        <TransactionModal
          teamId={teamId} categories={categories} initial={editTxn}
          defaultSeasonId={currentSeasonId}
          onClose={() => setEditTxn(null)}
          onSaved={() => { setEditTxn(null); refreshAll(); }}
        />
      )}
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────
function OverviewTab({ summary, collections }) {
  const byCurrency = summary.byCurrency || [];
  const byCategory = summary.byCategory || [];
  return (
    <div>
      {byCurrency.length === 0 && (
        <div style={{ ...card, textAlign: 'center', color: 'var(--text-2)' }}>
          No transactions yet. Add an expense or income to get started.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 18 }}>
        {byCurrency.map(c => (
          <div key={c.currency} style={card}>
            <div style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 700, textTransform: 'uppercase' }}>{c.currency?.toUpperCase()} balance</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: c.net_cents >= 0 ? 'var(--goal, #34d399)' : 'var(--penalty, #f87171)', margin: '4px 0 10px' }}>
              {fmtMoney(c.net_cents, c.currency)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-2)' }}>
              <span>Income</span><span style={{ color: 'var(--goal,#34d399)' }}>{fmtMoney(c.income_cents, c.currency)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-2)' }}>
              <span>Expenses</span><span style={{ color: 'var(--penalty,#f87171)' }}>{fmtMoney(c.expense_cents, c.currency)}</span>
            </div>
          </div>
        ))}
        {/* 2026-07-10 (Phase 5.3) — estimated cost per FULL-TIME player:
            total expenses ÷ FT non-goalie player count (divisor per
            Gilles: full-time players only; backend computes the count
            fresh on every summary load, so this auto-updates as the
            roster changes). One card per currency with expenses. */}
        {Number(summary.fullTimePlayerCount) > 0 && byCurrency.filter(c => c.expense_cents > 0).map(c => (
          <div key={`cpp-${c.currency}`} style={card}>
            <div style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 700, textTransform: 'uppercase' }}>
              Est. cost per full-time player{byCurrency.filter(x => x.expense_cents > 0).length > 1 ? ` (${c.currency?.toUpperCase()})` : ''}
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--ice, #60a5fa)', margin: '4px 0 10px' }}>
              {fmtMoney(Math.round(c.expense_cents / summary.fullTimePlayerCount), c.currency)}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
              {fmtMoney(c.expense_cents, c.currency)} expenses ÷ {summary.fullTimePlayerCount} full-time player{summary.fullTimePlayerCount !== 1 ? 's' : ''} (goalies excluded)
            </div>
          </div>
        ))}
      </div>

      {collections?.totals && Object.keys(collections.totals).length > 0 && (
        <div style={{ ...card, marginBottom: 18 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Collected fees (actual)</h3>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-2)' }}>
            Money actually collected through the app (Stripe + recorded payments), vs your budgeted income. Per-player detail is on the Finance tab.
          </p>
          {/* mobile pass — narrow-viewport horizontal-scroll wrapper + minWidth so the
              4-column money table can't get squished/clipped at ~390px (same pattern
              already used elsewhere in the finance tabs); desktop is unaffected since
              the table already fits within its card at normal widths. */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 480 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-3)', fontSize: 12 }}>
                  <th style={{ padding: '6px 4px' }}>Currency</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Collected</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Pending</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Budgeted income</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(collections.totals).map(([ccy, t]) => {
                  const budgeted = byCurrency.find(c => c.currency === ccy);
                  return (
                    <tr key={ccy} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 4px' }}>{ccy.toUpperCase()}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right', color: 'var(--goal,#34d399)' }}>{fmtMoney(t.collectedCents, ccy)}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right', color: 'var(--text-3)' }}>{fmtMoney(t.pendingCents, ccy)}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right' }}>{budgeted ? fmtMoney(budgeted.income_cents, ccy) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {byCategory.length > 0 && (
        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>By category</h3>
          {/* mobile pass — same horizontal-scroll wrapper as above. */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 420 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-3)', fontSize: 12 }}>
                  <th style={{ padding: '6px 4px' }}>Category</th>
                  <th style={{ padding: '6px 4px' }}>Type</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Total</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>#</th>
                </tr>
              </thead>
              <tbody>
                {byCategory.map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 4px' }}>{r.category_name}</td>
                    <td style={{ padding: '8px 4px', color: r.kind === 'income' ? 'var(--goal,#34d399)' : 'var(--penalty,#f87171)' }}>{r.kind}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'right' }}>{fmtMoney(r.total_cents, r.currency)}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'right', color: 'var(--text-3)' }}>{r.txn_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Transactions ──────────────────────────────────────────────────────
function TransactionsTab({ txns, categories, seasons, fSeason, setFSeason, fKind, setFKind, fCategory, setFCategory, onEdit, teamId, onChanged }) {
  const del = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try { await api.delete(`/teams/${teamId}/budget/transactions/${id}`); toast.success('Deleted'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  };
  const exportCsv = () => {
    const head = ['Date', 'Type', 'Category', 'Payee', 'Memo', 'Method', 'Amount', 'Currency', 'Status'];
    const rows = txns.map(t => [t.txn_date, t.kind, t.category_name || '', t.payee || '', t.memo || '', t.payment_method, (t.amount_cents / 100).toFixed(2), t.currency, t.status]);
    const csv = [head, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `budget-${teamId}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <select style={{ ...inputStyle, width: 'auto' }} value={fKind} onChange={e => setFKind(e.target.value)}>
          <option value="all">All types</option><option value="income">Income</option><option value="expense">Expense</option>
        </select>
        <select style={{ ...inputStyle, width: 'auto' }} value={fCategory} onChange={e => setFCategory(e.target.value)}>
          <option value="all">All categories</option>
          {categories.filter(c => !c.is_archived).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {seasons.length > 0 && (
          <select style={{ ...inputStyle, width: 'auto' }} value={fSeason} onChange={e => setFSeason(e.target.value)}>
            <option value="all">All seasons</option>
            {seasons.map(s => <option key={s} value={s}>Season {s.slice(0, 8)}</option>)}
          </select>
        )}
        <button style={{ ...btn, marginLeft: 'auto' }} onClick={exportCsv}><Download size={14} style={{ verticalAlign: -2, marginRight: 4 }} />CSV</button>
        <button style={btn} onClick={() => onEdit({ kind: 'income', txn_date: todayISO(), currency: 'cad' })}>+ Income</button>
        <button style={btnPrimary} onClick={() => onEdit({ kind: 'expense', txn_date: todayISO(), currency: 'cad' })}>+ Expense</button>
      </div>

      <div style={card}>
        {txns.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-2)', padding: 20 }}>No transactions match these filters.</div>
        ) : (
          // mobile pass — horizontal-scroll wrapper + minWidth: 5 columns
          // (Date/Category/Payee/Amount/actions) would otherwise squish
          // Payee text and the edit/delete buttons at ~390px.
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 520 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-3)', fontSize: 12 }}>
                  <th style={{ padding: '6px 4px' }}>Date</th>
                  <th style={{ padding: '6px 4px' }}>Category</th>
                  <th style={{ padding: '6px 4px' }}>Payee</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Amount</th>
                  <th style={{ padding: '6px 4px' }}></th>
                </tr>
              </thead>
              <tbody>
                {txns.map(t => (
                  <tr key={t.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 4px', whiteSpace: 'nowrap' }}>{t.txn_date}</td>
                    <td style={{ padding: '8px 4px' }}>{t.category_name || <span style={{ color: 'var(--text-3)' }}>—</span>}{t.memo ? <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{t.memo}</div> : null}</td>
                    <td style={{ padding: '8px 4px' }}>{t.payee || <span style={{ color: 'var(--text-3)' }}>—</span>}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 700, color: t.kind === 'income' ? 'var(--goal,#34d399)' : 'var(--penalty,#f87171)' }}>
                      {t.kind === 'income' ? '+' : '−'}{fmtMoney(t.amount_cents, t.currency)}
                    </td>
                    <td style={{ padding: '8px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button style={{ ...btn, padding: '4px 8px' }} onClick={() => onEdit(t)}><Pencil size={13} /></button>
                      <button style={{ ...btn, padding: '4px 8px', marginLeft: 4, color: 'var(--penalty,#f87171)' }} onClick={() => del(t.id)}><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Transaction add/edit modal ────────────────────────────────────────
function TransactionModal({ teamId, categories, initial, defaultSeasonId, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [kind, setKind] = useState(initial.kind || 'expense');
  const [amount, setAmount] = useState(initial.amount_cents != null ? (initial.amount_cents / 100).toFixed(2) : '');
  const [currency, setCurrency] = useState(initial.currency || 'cad');
  const [date, setDate] = useState(initial.txn_date || todayISO());
  const [categoryId, setCategoryId] = useState(initial.category_id || '');
  const [payee, setPayee] = useState(initial.payee || '');
  const [memo, setMemo] = useState(initial.memo || '');
  const [method, setMethod] = useState(initial.payment_method || 'other');
  const [saving, setSaving] = useState(false);

  const cats = categories.filter(c => c.kind === kind && !c.is_archived);

  const save = async () => {
    const cents = centsFromInput(amount);
    if (cents == null) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    const body = {
      kind, amount_cents: cents, currency, txn_date: date,
      category_id: categoryId || null, payee, memo, payment_method: method,
      season_id: initial.season_id || defaultSeasonId || null,
      receipt_id: initial.receipt_id || null,
      source: initial.receipt_id ? 'receipt' : 'manual',
    };
    try {
      if (isEdit) await api.patch(`/teams/${teamId}/budget/transactions/${initial.id}`, body);
      else await api.post(`/teams/${teamId}/budget/transactions`, body);
      toast.success(isEdit ? 'Updated' : 'Added');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      {/* mobile pass — was plain 90vh, which doesn't shrink when the mobile
          keyboard opens (this modal has 3 text/select inputs incl. Amount
          which is often focused first). var(--mobile-h) tracks the actual
          visible viewport and falls back to 100dvh on desktop, so desktop
          rendering is unchanged. See SparesTab.jsx CashPaymentModal for the
          same pattern already established elsewhere. */}
      <div onClick={e => e.stopPropagation()} style={{ ...card, width: '100%', maxWidth: 460, maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>{isEdit ? 'Edit' : 'Add'} {kind}</h3>
          <button style={{ ...btn, padding: '4px 8px' }} onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {['expense', 'income'].map(k => (
            <button key={k} onClick={() => { setKind(k); setCategoryId(''); }}
              style={{ ...btn, flex: 1, background: kind === k ? 'var(--ice-a12,rgba(56,189,248,0.12))' : 'transparent', color: kind === k ? 'var(--ice)' : 'var(--text-2)', textTransform: 'capitalize' }}>{k}</button>
          ))}
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={label}>Amount</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={inputStyle} type="text" inputMode="decimal" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} />
            <select style={{ ...inputStyle, width: 90 }} value={currency} onChange={e => setCurrency(e.target.value)}>
              <option value="cad">CAD</option><option value="usd">USD</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={label}>Date</label>
          <input style={inputStyle} type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={label}>Category</label>
          <select style={inputStyle} value={categoryId} onChange={e => setCategoryId(e.target.value)}>
            <option value="">— none —</option>
            {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={label}>{kind === 'income' ? 'From (payer)' : 'Payee / vendor'}</label>
          <input style={inputStyle} type="text" value={payee} onChange={e => setPayee(e.target.value)} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={label}>Method</label>
          <select style={inputStyle} value={method} onChange={e => setMethod(e.target.value)}>
            {['cash', 'etransfer', 'cheque', 'stripe', 'card', 'other'].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Memo</label>
          <input style={inputStyle} type="text" value={memo} onChange={e => setMemo(e.target.value)} />
        </div>
        <button style={{ ...btnPrimary, width: '100%' }} disabled={saving} onClick={save}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : `Add ${kind}`)}</button>
      </div>
    </div>
  );
}

// ── Receipts ──────────────────────────────────────────────────────────
function ReceiptsTab({ teamId, receipts, onChanged, categories, onPrefill }) {
  const [busy, setBusy] = useState(false);

  // Map an AI-extracted receipt onto a prefilled expense (opens the modal).
  const prefill = (ex, receiptId) => {
    if (!ex) return;
    const match = (categories || []).find(c => c.kind === 'expense' && ex.suggested_category && c.name.toLowerCase() === String(ex.suggested_category).toLowerCase());
    onPrefill({
      kind: 'expense',
      amount_cents: ex.total_cents != null ? ex.total_cents : ex.subtotal_cents,
      currency: ex.currency || 'cad',
      txn_date: ex.date || todayISO(),
      payee: ex.merchant || '',
      category_id: match ? match.id : '',
      receipt_id: receiptId,
      memo: '',
    });
  };

  // Upload → the backend auto-processes with the dual AI engines and returns
  // the extracted fields in the same response, so we prefill immediately.
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const t = toast.loading('Uploading & reading receipt…');
    try {
      const { data } = await api.post(`/teams/${teamId}/budget/receipts`, file, { headers: { 'Content-Type': file.type } });
      onChanged();
      if (data.extracted) {
        toast.success('Receipt read — review the expense', { id: t });
        prefill(data.extracted, data.receipt.id);
      } else {
        toast.success('Uploaded — AI could not read it; enter the expense manually or hit Re-scan.', { id: t });
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Upload failed', { id: t }); }
    finally { setBusy(false); }
  };

  // Re-run the AI on an already-uploaded receipt.
  const scan = async (id) => {
    setBusy(true);
    const t = toast.loading('Reading receipt with AI…');
    try {
      const { data } = await api.post(`/teams/${teamId}/budget/receipts/${id}/scan`);
      toast.success('Receipt read', { id: t });
      onChanged();
      prefill(data.extracted, id);
    } catch (e) { toast.error(e.response?.data?.error || 'Could not read receipt', { id: t }); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this receipt?')) return;
    try { await api.delete(`/teams/${teamId}/budget/receipts/${id}`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  };

  const view = async (r) => {
    try {
      const res = await api.get(`/teams/${teamId}/budget/receipts/${r.id}/view`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch { toast.error('Could not open receipt'); }
  };

  return (
    <div>
      <div style={{ ...card, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Sparkles size={18} style={{ color: 'var(--ice)' }} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 700 }}>Scan a receipt</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Upload a photo or PDF — AI reads the merchant, date, and total, then pre-fills an expense.</div>
        </div>
        <label style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Upload size={15} /> {busy ? 'Working…' : 'Upload receipt'}
          <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} disabled={busy}
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; upload(f); }} />
        </label>
      </div>

      <div style={card}>
        {receipts.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-2)', padding: 20 }}>No receipts yet.</div>
        ) : (
          // mobile pass — the info cell (icon + date + size + merchant +
          // amount) plus 3 action buttons (View/Re-scan/Delete) in the
          // second cell would squish badly at ~390px without a horizontal
          // scroll fallback.
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 480 }}>
              <tbody>
                {receipts.map(r => (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 4px' }}>
                      <ReceiptIcon size={14} style={{ verticalAlign: -2, marginRight: 6, color: 'var(--text-3)' }} />
                      {(r.created_at || '').slice(0, 10)} · {(r.byte_size / 1024).toFixed(0)} KB
                      {r.ai_extracted?.merchant ? <span style={{ color: 'var(--text-2)' }}> · {r.ai_extracted.merchant}</span> : null}
                      {r.ai_extracted?.total_cents != null ? <span style={{ color: 'var(--text-2)' }}> · {fmtMoney(r.ai_extracted.total_cents, r.ai_extracted.currency)}</span> : null}
                    </td>
                    <td style={{ padding: '8px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button style={{ ...btn, padding: '4px 8px' }} onClick={() => view(r)}>View</button>
                      <button style={{ ...btn, padding: '4px 8px', marginLeft: 4 }} onClick={() => scan(r.id)} disabled={busy}>Re-scan</button>
                      <button style={{ ...btn, padding: '4px 8px', marginLeft: 4, color: 'var(--penalty,#f87171)' }} onClick={() => del(r.id)}><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Categories ────────────────────────────────────────────────────────
function CategoriesTab({ teamId, categories, onChanged }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('expense');
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post(`/teams/${teamId}/budget/categories`, { name, kind }); setName(''); onChanged(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const archive = async (id) => {
    try { await api.delete(`/teams/${teamId}/budget/categories/${id}`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const groups = [['expense', 'Expense'], ['income', 'Income']];
  return (
    <div>
      <div style={{ ...card, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label style={label}>New category</label>
          <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Goalie equipment" />
        </div>
        <select style={{ ...inputStyle, width: 130 }} value={kind} onChange={e => setKind(e.target.value)}>
          <option value="expense">Expense</option><option value="income">Income</option>
        </select>
        <button style={btnPrimary} onClick={add}><Plus size={15} style={{ verticalAlign: -2 }} /> Add</button>
      </div>
      {groups.map(([k, lbl]) => (
        <div key={k} style={{ ...card, marginBottom: 12 }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 15 }}>{lbl}</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {categories.filter(c => c.kind === k && !c.is_archived).map(c => (
              <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, background: 'var(--surface-2)', border: '1px solid var(--border)', fontSize: 13 }}>
                {c.name}
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 0 }} title="Archive" onClick={() => archive(c.id)}><X size={13} /></button>
              </span>
            ))}
            {categories.filter(c => c.kind === k && !c.is_archived).length === 0 && <span style={{ color: 'var(--text-3)', fontSize: 13 }}>None yet</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Wallet (Model B) ──────────────────────────────────────────────────
function WalletTab({ teamId, wallet, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [dep, setDep] = useState('');
  const [pay, setPay] = useState('');

  if (wallet === undefined) return <div style={card}>Loading…</div>;

  const enable = async () => {
    setBusy(true);
    try { await api.post(`/teams/${teamId}/budget/wallet/enable`, { currency: 'cad' }); toast.success('Managed wallet enabled'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };
  const deposit = async () => {
    const c = centsFromInput(dep); if (!c) { toast.error('Enter an amount'); return; }
    setBusy(true);
    try { await api.post(`/teams/${teamId}/budget/wallet/deposit`, { amount_cents: c, currency: wallet.wallet.currency }); setDep(''); toast.success('Deposit recorded'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };
  const requestPayout = async () => {
    const c = centsFromInput(pay); if (!c) { toast.error('Enter an amount'); return; }
    setBusy(true);
    try { await api.post(`/teams/${teamId}/budget/wallet/payouts`, { amount_cents: c, currency: wallet.wallet.currency, destination_kind: 'etransfer' }); setPay(''); toast.success('Payout requested'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };

  if (wallet === null) {
    return (
      <div style={{ ...card, textAlign: 'center' }}>
        <WalletIcon size={28} style={{ color: 'var(--ice)' }} />
        <h3 style={{ margin: '10px 0 6px' }}>Managed trust wallet</h3>
        <p style={{ color: 'var(--text-2)', fontSize: 14, maxWidth: 520, margin: '0 auto 14px' }}>
          For teams that can't open their own bank/Stripe account, Game Day can collect your fees and
          hold them in trust, tracking your balance here. Money-out (payouts) is reviewed by a Game Day
          admin. <strong>Note:</strong> this is a custodial feature with legal/compliance requirements —
          it's bookkeeping-ready, but real payouts are gated until those are in place.
        </p>
        <button style={btnPrimary} disabled={busy} onClick={enable}>Enable managed wallet</button>
      </div>
    );
  }

  const w = wallet.wallet;
  return (
    <div>
      <div style={{ ...card, marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 700, textTransform: 'uppercase' }}>Wallet balance ({w.currency?.toUpperCase()})</div>
        <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--ice)', margin: '4px 0' }}>{fmtMoney(wallet.balance_cents, w.currency)}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Status: {w.status} · {w.provider}</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 12, marginBottom: 14 }}>
        <div style={card}>
          <label style={label}>Record a deposit</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={inputStyle} inputMode="decimal" placeholder="0.00" value={dep} onChange={e => setDep(e.target.value)} />
            <button style={btnPrimary} disabled={busy} onClick={deposit}>Add</button>
          </div>
        </div>
        <div style={card}>
          <label style={label}>Request a payout</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={inputStyle} inputMode="decimal" placeholder="0.00" value={pay} onChange={e => setPay(e.target.value)} />
            <button style={btn} disabled={busy} onClick={requestPayout}>Request</button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>A Game Day admin reviews & sends payouts.</div>
        </div>
      </div>

      <div style={card}>
        <h3 style={{ margin: '0 0 10px', fontSize: 15 }}>Recent activity</h3>
        {(wallet.entries || []).length === 0 ? (
          <div style={{ color: 'var(--text-2)', fontSize: 14 }}>No entries yet.</div>
        ) : (
          // mobile pass — memo text in the first cell can run long; wrap
          // in the same horizontal-scroll pattern as the other tables
          // above rather than letting it clip.
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 400 }}>
              <tbody>
                {wallet.entries.map(e => (
                  <tr key={e.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 4px' }}>{(e.created_at || '').slice(0, 10)}<div style={{ fontSize: 12, color: 'var(--text-3)' }}>{e.entry_type}{e.memo ? ` · ${e.memo}` : ''}</div></td>
                    <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 700, color: e.direction === 'credit' ? 'var(--goal,#34d399)' : 'var(--penalty,#f87171)' }}>
                      {e.direction === 'credit' ? '+' : '−'}{fmtMoney(e.amount_cents, e.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
